/**
 * @fileoverview Event card component.
 * @summary Displays an event card with basic information and actions.
 *
 * @package KaraoNow
 * @module apps/front/src/components/EventCard
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import type { Event } from '../store/slices/eventsSlice.js'
import { formatDate } from '../utils/date.js'
import EventPlanBadge from '../pages/organizer/components/EventPlanBadge.js'
import { getOneShotDaysRemaining, formatOneShotExpirationDate, shouldShowOneShotExpiration } from '../utils/oneShotHelpers.js'
import { SubscriptionType } from '@karaonow/shared'
import { ConfirmModal } from './ConfirmModal.js'

/**
 * @summary Event card props.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
interface EventCardProps {
  event: Event
  onDelete?: (eventId: string) => void
}

/**
 * @summary Get status badge color class.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
function getStatusBadgeClass(status: Event['status']): string {
  switch (status) {
    case 'draft':
      return 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
    case 'active':
      return 'bg-green-200 text-green-800 dark:bg-green-800 dark:text-green-200'
    case 'completed':
      return 'bg-blue-200 text-blue-800 dark:bg-blue-800 dark:text-blue-200'
    case 'cancelled':
      return 'bg-red-200 text-red-800 dark:bg-red-800 dark:text-red-200'
    case 'hidden':
      return 'bg-gray-400 text-gray-900 dark:bg-gray-600 dark:text-gray-100'
    default:
      return 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
  }
}



/**
 * @summary Event card component.
 * @returns {React.ReactElement} Event card.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export default function EventCard({ event, onDelete }: EventCardProps) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [showHiddenModal, setShowHiddenModal] = useState(false)

  const isHidden = event.status === 'hidden'

  const handleCardClick = () => {
    if (isHidden) {
      setShowHiddenModal(true)
      return
    }
    navigate(`/events/${event.id}`)
  }

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    onDelete?.(event.id)
  }

  const handleViewClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isHidden) {
      setShowHiddenModal(true)
      return
    }
    navigate(`/events/${event.id}`)
  }

  return (
    <>
    <div
      onClick={handleCardClick}
      className={`card hover:shadow-lg transition-shadow ${isHidden ? 'bg-gray-300! dark:bg-gray-900!' : 'cursor-pointer'}`}
    >
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <h3 className="text-xl font-bold mb-2">{event.name}</h3>
          {event.description && (
            <p className="text-sm text-(--color-text-secondary) mb-3 line-clamp-2">
              {event.description}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Plan Badge */}
          <EventPlanBadge planType={event.planType || SubscriptionType.FREE} size="sm" />
          {/* Status Badge */}
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadgeClass(event.status)}`}>
            {t(`events.status${event.status.charAt(0).toUpperCase() + event.status.slice(1)}`)}
          </span>
          {/* Hidden Badge */}
          {/* {event.status === 'hidden' && (
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-500 text-white">
              {t('events.hidden') || 'Masqué'}
            </span>
          )} */}
        </div>
      </div>

      <div className="space-y-2 text-sm text-(--color-text-tertiary) mb-4">
        <div>
          <span className="font-medium">{t('events.date')}:</span>{' '}
          <span>{formatDate(event.date, i18n.language)}</span>
        </div>
        {/* ONE_SHOT Expiration Date */}
        {shouldShowOneShotExpiration(event) && (
          <div className={`flex items-start gap-2 text-sm ${event.status === 'completed' ? 'text-gray-400 dark:text-gray-500 line-through' : 'text-green-700 dark:text-green-400'}`}>
            <span className="font-medium">{t('subscription.expiresLabel') || 'Expire le'}:</span>
            <span className={event.status === 'completed' 
              ? 'line-through' 
              : getOneShotDaysRemaining(event.oneShotExpiresAt) <= 3 ? 'text-orange-600 dark:text-orange-400 font-semibold' : ''}>
              {formatOneShotExpirationDate(event.oneShotExpiresAt, i18n.language)}
              {' '}
              ({t('subscription.oneShotDaysRemaining', { count: getOneShotDaysRemaining(event.oneShotExpiresAt) })})
            </span>
          </div>
        )}
      </div>

      <div className="flex gap-2 pt-4 border-t border-(--color-border)">
        <button
          type="button"
          onClick={handleViewClick}
          disabled={isHidden}
          className={`btn btn-sm flex-1 ${isHidden ? 'btn-disabled opacity-50 cursor-not-allowed' : 'btn-primary'}`}
          title={isHidden ? t('events.hiddenEventTooltip') || 'Cet événement est masqué et ne peut plus être consulté' : undefined}
        >
          {t('events.open')}
        </button>
        {onDelete && (event.status === 'completed' || event.status === 'cancelled') && (
          <button
            type="button"
            onClick={handleDelete}
            className="btn btn-secondary btn-sm"
            title={t('events.hide') || 'Masquer l\'événement'}
          >
            {t('events.hide') || 'Masquer'}
          </button>
        )}
      </div>
    </div>
    
    {/* Hidden Event Info Modal */}
    <ConfirmModal
      isOpen={showHiddenModal}
      onCancel={() => setShowHiddenModal(false)}
      onConfirm={() => setShowHiddenModal(false)}
      title={t('events.hiddenEventTitle') || 'Événement masqué'}
      message={t('events.hiddenEventMessage') || 'Cet événement a été masqué et n\'est plus accessible. Il reste comptabilisé dans votre quota mensuel.'}
      confirmLabel={t('common.close') || 'Fermer'}
      variant="info"
      infoOnly={true}
    />
    </>
  )
}

